import type { PropsWithChildren, SyntheticEvent } from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { getCurrentSanityPage } from 'components/sanity/analytics';
import { staticSiteNavClick, staticSiteNavOpened } from 'utils/metrics';
import {
  toNavClickProperties,
  toNavOpenedProperties,
  type NavItemIdentity,
  type NavPage,
  type NavPlacement,
  type NavRegion,
  type NavSurface,
} from './navEvents';
import { createNavOpenDetector } from './navOpenDetector';

type NavTracking = NavPlacement & { enabled: boolean };

type NavTrackingProviderProps = PropsWithChildren<{
  enabled?: boolean;
  item?: NavItemIdentity;
  region?: NavRegion;
  surface?: NavSurface;
}>;

const NavTrackingContext = createContext<NavTracking>({ chain: [], enabled: false, region: 'main_nav', surface: 'desktop_horizontal' });

const getCurrentPage = (): NavPage => ({
  host: window.location.host,
  path: window.location.pathname,
  sanityPage: getCurrentSanityPage(),
});

const withItem = (tracking: NavTracking, item?: NavItemIdentity): NavTracking =>
  item ? { ...tracking, chain: [...tracking.chain, item] } : tracking;

export function NavTrackingProvider({ children, item, ...overrides }: NavTrackingProviderProps) {
  const parent = useContext(NavTrackingContext);

  return <NavTrackingContext.Provider value={withItem({ ...parent, ...overrides }, item)}>{children}</NavTrackingContext.Provider>;
}

export function useNavTracking(item?: NavItemIdentity) {
  const tracking = withItem(useContext(NavTrackingContext), item);
  const isTracked = tracking.enabled && tracking.chain.length > 0;

  return {
    trackClick: (ev: SyntheticEvent<Element>, href: string) => {
      if (isTracked) {
        staticSiteNavClick(toNavClickProperties(tracking, getCurrentPage(), { href, text: ev.currentTarget.textContent ?? undefined }));
      }
    },
    trackOpen: () => {
      if (isTracked) {
        staticSiteNavOpened(toNavOpenedProperties(tracking, getCurrentPage()));
      }
    },
  };
}

export function useNavOpenDetector(closeDelayMs: number) {
  const { trackOpen } = useNavTracking();
  const trackOpenRef = useRef(trackOpen);
  const [detector] = useState(() => createNavOpenDetector(() => trackOpenRef.current(), closeDelayMs));

  useEffect(() => {
    trackOpenRef.current = trackOpen;
  });

  useEffect(() => () => detector.dispose(), [detector]);

  return detector;
}
